/**
 * Who may reach a route, and who may manage journeys.
 *
 * Pure functions taking the user explicitly, so the sidebar, the route guard
 * and `npm run test:unit` all exercise the same code. `authStore` wraps these
 * with the signed-in user; nothing should reimplement the rules.
 *
 * The rule that matters: **the sidebar and the guard must answer identically.**
 * They used to hold separate copies, which is survivable right up until a route
 * grows a condition only one of them knows about — and then the menu offers a
 * page the guard bounces you off, which reads as a broken dashboard rather than
 * as a permission.
 */

export const ROLE_ROOT_ADMIN = "root_admin";
export const ROLE_KINDERGARTEN_ADMIN = "kindergarten_admin";
export const ROLE_KINDERGARTEN_USER = "kindergarten_user";
export const ROLE_DRIVER = "driver";
export const ROLE_PARENT = "parent";
/** Wosool staff who keep Customers from the app — refused here, see below. */
export const ROLE_SALES_AGENT = "sales_agent";
export const ROLE_ACCOUNTANT = "accountant";
/** A school's teacher (Education). Works from the Wosool app only — refused here. */
export const ROLE_TEACHER = "teacher";

/** Roles that run a school's office. */
const STAFF_ROLES = [
  ROLE_ROOT_ADMIN,
  ROLE_KINDERGARTEN_ADMIN,
  ROLE_KINDERGARTEN_USER,
];

/**
 * Every role this dashboard has screens for. Any other is refused after
 * sign-in (`isSignedInUserBlocked`): the sales agent and the teacher, who work
 * from the app, and any role a later backend adds before this build knows
 * what to show it.
 */
const DASHBOARD_ROLES = [
  ...STAFF_ROLES,
  ROLE_ACCOUNTANT,
  ROLE_DRIVER,
  ROLE_PARENT,
];

/**
 * Whether this user may author their school's journeys.
 *
 * True for school staff by role, and for a bus supervisor the school has put
 * in charge — `role: "driver"` plus `manages_journeys`. Deliberately not a
 * role of its own: a released mobile build resolves an unknown role to
 * `parent`, so inventing one would sign that person into the parent app.
 *
 * The backend is the authority on this; it re-reads the flag from the user
 * document on every journey call. This is only what the UI draws.
 */
export function canManageJourneys(user) {
  if (!user) return false;
  if (STAFF_ROLES.includes(user.role)) return true;
  return user.role === ROLE_DRIVER && user.manages_journeys === true;
}

/**
 * Whether this user is one of the school's office staff.
 *
 * The gate for the navbar's office tools — global search, Print, Export, the
 * academic-year chip — every one of which calls a staff-only endpoint. A
 * journey manager reaches this dashboard but is not office staff: left
 * mounted, those tools fire background requests that 403 and raise a toast on
 * a page the person is using correctly.
 */
export function isOfficeStaff(user) {
  return Boolean(user) && STAFF_ROLES.includes(user.role);
}

/**
 * Whether this user is the school's accountant.
 *
 * Runs Financial Management and only *reads* parents, children and journeys.
 * Deliberately not in `STAFF_ROLES`: that would hand them journey editing and
 * the office tools. Every page they share with staff asks this to hide its
 * actions; the backend refuses those calls regardless (its accountant gate).
 */
export function isAccountant(user) {
  return Boolean(user) && user.role === ROLE_ACCOUNTANT;
}

/**
 * Whether this user may open the journeys pages at all — to edit them, or
 * (the accountant) to read them. The pages draw read-only for anyone who
 * cannot also `canManageJourneys`.
 */
export function canViewJourneys(user) {
  return canManageJourneys(user) || isAccountant(user);
}

/**
 * Every capability a route may require, and how to answer it.
 *
 * The registry is the closed set: `canAccessRoute` refuses anything not in
 * here rather than waving it through. Keep it in step with the
 * `RouteCapability` union in `authStore.ts`.
 */
const CAPABILITY_CHECKS = new Map([
  ["manages_journeys", canManageJourneys],
  ["views_journeys", canViewJourneys],
]);

/**
 * Whether this user may reach a route.
 *
 * `requiresCapability` **narrows** `allowedRoles`, never widens it: a route
 * naming DRIVER plus `manages_journeys` opens to that one supervisor, and to
 * the staff roles it also names. A route with no `allowedRoles` is open to any
 * signed-in user, as it always has been.
 */
export function canAccessRoute(user, route) {
  const { allowedRoles, requiresCapability } = route || {};

  if (allowedRoles && allowedRoles.length > 0) {
    if (!user || !allowedRoles.includes(user.role)) return false;
  }

  if (requiresCapability) {
    // A `Map`, not an object literal: `CAPABILITY_CHECKS["constructor"]` on a
    // literal resolves off `Object.prototype` to a function, and calling it
    // returns a truthy boxed object — a route whose capability was typo'd as
    // an inherited member name would grant access to everyone.
    const check = CAPABILITY_CHECKS.get(requiresCapability);
    // An unrecognised capability is refused, not ignored. Route definitions
    // live in `useManagerRoutes.js` — plain JavaScript, so the
    // `RouteCapability` union in authStore.ts does not typecheck them and a
    // typo is entirely possible. Ignoring it would silently open `/trips` to
    // every driver the role list names; refusing makes the typo a dark menu
    // item somebody reports the same day.
    if (!check) return false;
    return check(user);
  }

  return true;
}

/**
 * Whether a route should appear in the sidebar for this user.
 *
 * Encodes one rule that is easy to get wrong in a render loop, and did:
 * **a collapse group is visible exactly when something inside it is.** A
 * group's own `allowedRoles` is a hand-written summary of its children's, and
 * it goes stale the moment one child's list widens — "Tracking & Buses" did
 * not name DRIVER, so a journey manager who may reach Trips Management inside
 * it got an empty sidebar and no route back to the journeys.
 *
 * So the group itself is never role-checked; its contents decide.
 */
export function isRouteVisibleInSidebar(user, route) {
  if (!route || route.redirect || route.hideFromSidebar) return false;

  if (route.collapse) {
    return (route.views || []).some((view) => canAccessRoute(user, view));
  }

  return canAccessRoute(user, route);
}

/**
 * Whether a role the phone check lists is refused before the password step.
 *
 * The PRE-login gate, answered from a role alone — `check_phone_number` is
 * anonymous and says nothing else. `isSignedInUserBlocked` is the real gate.
 *
 * - **A teacher, always.** Teachers work from the Wosool app; the driver/parent
 *   switch is for school accounts that have pages here, and a teacher has
 *   none. The backend hides teacher rows from the dashboard and refuses to
 *   sign one in here (`teacher_app_only`); this gate and
 *   `isSignedInUserBlocked` are the dashboard's own locks.
 * - **A parent, while the switch is off** — refused here, where it saves them
 *   typing a password for a door that will not open.
 * - **Never a driver.** The phone check does not say whether a supervisor
 *   manages journeys, so they are decided after the password.
 *
 * Takes the switch as an argument, like `isSignedInUserBlocked`, so it is
 * testable without the build environment.
 */
export function isRoleBlockedBeforeSignIn(role, driverParentAccessEnabled) {
  if (role === ROLE_TEACHER) return true;
  if (driverParentAccessEnabled) return false;
  return role === ROLE_PARENT;
}

/**
 * Whether this signed-in user is shut out of the web dashboard entirely.
 *
 * The POST-login gate. Deny by default: only the roles this dashboard has
 * screens for get past the first check. Drivers and parents are then
 * mobile-only unless `REACT_APP_ENABLE_DRIVER_PARENT_ACCESS` is on — with one
 * exception, which is the whole point of the capability: the supervisor a
 * school runs its journeys through belongs here.
 *
 * Takes `driverParentAccessEnabled` as an argument rather than reading
 * `process.env` so it is testable and has no import-time dependency on the
 * build environment.
 */
export function isSignedInUserBlocked(user, driverParentAccessEnabled) {
  if (!user) return false;
  // Before the driver/parent switch, on purpose: that switch opens the
  // dashboard to school accounts, and must never let in a sales agent or a
  // teacher, who work from the app — nor a role this build has never heard
  // of, which no check below would catch: it would stay signed in on a page
  // with nothing to show it. Pre-login there is little to test — the phone
  // check says only `is_root` for an agent, and never lists a teacher here.
  if (!DASHBOARD_ROLES.includes(user.role)) return true;
  if (driverParentAccessEnabled) return false;
  if (user.role === ROLE_PARENT) return true;
  if (user.role === ROLE_DRIVER) return !canManageJourneys(user);
  return false;
}
